import { useState } from "react";

import { CategoryIcon } from "@/components/DiscussionBits";
import { createThread } from "@/lib/api";
import type { Thread, ThreadCategory } from "@/lib/types";

// The label the author picks here is what the thread is filed under — it drives the chip on the
// thread row and the filter on the Discussions tab. "Review" used to be a third option; it
// duplicated the star-rating feature, so ratings is now the one place a skill gets reviewed.
const CATEGORIES: { id: ThreadCategory; label: string }[] = [
  { id: "question", label: "Question" },
  { id: "bug", label: "Bug" },
];

export default function NewThreadModal({
  slug,
  accessToken,
  onClose,
  onCreated,
}: {
  slug: string;
  accessToken: string;
  onClose: () => void;
  onCreated: (thread: Thread) => void;
}) {
  const [category, setCategory] = useState<ThreadCategory>("question");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const thread = await createThread(slug, { category, title: title.trim(), body }, accessToken);
      onCreated(thread);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't post — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#111] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-medium text-text">New discussion</h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text/40 transition hover:bg-white/10 hover:text-text"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <label className="mt-5 block font-mono text-[10px] uppercase tracking-[0.15em] text-text/40">Label</label>
        <div className="mt-2 flex gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(c.id)}
              className={`flex items-center gap-2 rounded-lg border px-3.5 py-2.5 font-display text-sm font-medium transition ${
                category === c.id ? "border-primary/40 bg-primary/10 text-primary" : "border-white/10 text-text/70 hover:border-white/20"
              }`}
            >
              <CategoryIcon category={c.id} className="h-3.5 w-3.5" /> {c.label}
            </button>
          ))}
        </div>

        <label className="mt-5 block font-mono text-[10px] uppercase tracking-[0.15em] text-text/40">Title (required)</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Does this work with 2 python repos?"
          className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm outline-none focus:border-primary"
        />

        <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.15em] text-text/40">Body (text + code blocks)</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Describe what you tried and what happened…"
          rows={5}
          className="mt-2 w-full resize-none rounded-lg border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm outline-none focus:border-primary"
        />

        {error && <p className="mt-3 text-sm text-red-400">{error}</p>}

        <div className="mt-5 flex justify-end gap-2.5">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-4 py-2 font-display text-sm font-medium text-text/70 transition hover:border-white/30"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="rounded-lg bg-primary px-4 py-2 font-display text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Posting…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
